import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Copy, ExternalLink, FileSignature, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { esign } from '../../lib/api';

const STATUS = { sent: 'bg-blue-100 text-blue-700', viewed: 'bg-amber-100 text-amber-800', signed: 'bg-emerald-100 text-emerald-700', declined: 'bg-red-100 text-red-700', void: 'bg-slate-100 text-slate-500' };

/**
 * Send an estimate or a service agreement for e-signature.
 * kind="estimate" needs `estimate`; kind="agreement" takes `contract` (optional).
 */
export default function SendForSignatureDialog({ open, onClose, kind, estimate, contract }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [byEmail, setByEmail] = useState(true);
  const [f, setF] = useState({ service: '', frequency: '', price: '', startDate: '', body: '' });
  const [created, setCreated] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setEmail(estimate?.customerEmail || contract?.email || '');
    setPhone(contract?.phone || '');
    setF({ service: contract?.plan || '', frequency: contract?.frequency || '', price: contract?.rate || '', startDate: contract?.startDate || '', body: '' });
    const refId = estimate?.id;
    esign.list(refId ? { refId } : (contract?.customerId ? { customerId: contract.customerId } : {}))
      .then((rows) => setHistory((rows || []).filter((r) => r.kind === kind).slice(0, 5))).catch(() => setHistory([]));
  }, [open, estimate, contract, kind]);

  const create = async () => {
    setBusy(true);
    try {
      const body = kind === 'estimate'
        ? { kind, refId: estimate.id, customerId: estimate.customerId, email, send: byEmail }
        : { kind, customerId: contract?.customerId, customerName: contract?.customer, email, send: byEmail,
            service: f.service, frequency: f.frequency, price: f.price, startDate: f.startDate, body: f.body || undefined,
            title: f.service ? `Service Agreement — ${f.service}` : 'Service Agreement' };
      const r = await esign.create(body);
      setCreated(r);
      toast.success(r.emailed ? `Emailed to ${r.emailed}` : 'Signing link ready — copy or text it to the customer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the signing link');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(created.url); toast.success('Link copied'); } catch { toast.error('Copy failed — select the link and copy it'); }
  };
  const smsHref = created ? `sms:${phone}${/iPhone|iPad/.test(navigator.userAgent) ? '&' : '?'}body=${encodeURIComponent(`Please review and sign: ${created.url}`)}` : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#8BC53F]" />{kind === 'estimate' ? `Send ${estimate?.id || 'estimate'} for signature` : 'Send service agreement for signature'}</DialogTitle></DialogHeader>
        {!created ? (
          <div className="space-y-3">
            {kind === 'agreement' && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Service</Label><Input value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} className="mt-1 h-9" placeholder="General Pest — Quarterly" /></div>
                  <div><Label className="text-xs">Frequency</Label><Input value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} className="mt-1 h-9" placeholder="Quarterly" /></div>
                  <div><Label className="text-xs">Price per service</Label><Input type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="mt-1 h-9" /></div>
                  <div><Label className="text-xs">Start date</Label><Input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} className="mt-1 h-9" /></div>
                </div>
                <div><Label className="text-xs">Agreement text (leave empty for the standard agreement)</Label><Textarea rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1" placeholder="Standard service agreement will be used" /></div>
              </>
            )}
            <div><Label className="text-xs">Customer email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={byEmail} onChange={(e) => setByEmail(e.target.checked)} className="w-4 h-4 accent-[#8BC53F]" />Email the link now (needs email set up)</label>
            <div><Label className="text-xs">Mobile number (to text the link from your phone)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 h-9" /></div>
            <p className="text-xs text-slate-500">The document is frozen when you send it: later edits don’t change what the customer signs. Signing an estimate marks it Accepted.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs break-all" data-testid="esign-link">{created.url}</div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="gap-1.5" onClick={copy}><Copy className="w-4 h-4" />Copy link</Button>
              <Button variant="outline" className="gap-1.5" asChild><a href={smsHref}><MessageSquare className="w-4 h-4" />Text it</a></Button>
              <Button variant="outline" className="gap-1.5" asChild><a href={created.url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" />Preview</a></Button>
            </div>
          </div>
        )}
        {history.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-slate-500 mb-1">Earlier signature requests</div>
            {history.map((h) => (
              <div key={h.id} className="flex justify-between text-xs py-1">
                <span className="truncate mr-2">{h.title} · {new Date(h.createdAt).toLocaleDateString()}</span>
                <Badge className={`border-0 ${STATUS[h.status] || ''}`}>{h.status}{h.signerName ? ` · ${h.signerName}` : ''}</Badge>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!created && <Button className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" disabled={busy} onClick={create} data-testid="esign-create"><Send className="w-4 h-4" />{busy ? 'Creating…' : 'Create signing link'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
